import { BannerAlertVariant } from '@aurora/shared-client/components/common/BannerAlert/BannerAlert';
import {
  ToastAlertVariant,
  ToastVariant
} from '@aurora/shared-client/components/common/ToastAlert/enums';
import type ToastProps from '@aurora/shared-client/components/common/ToastAlert/ToastAlertProps';
import useFrameEnd from '@aurora/shared-client/components/context/AnalyticsParentFrames/useFrameEnd';
import ParentFrameSupplier from '@aurora/shared-utils/analytics/ParentFrameSupplier';
import TenantContext from '@aurora/shared-client/components/context/TenantContext';
import ToastContextProvider from '@aurora/shared-client/components/context/ToastContext/ToastContext';
import useAuthFlow from '@aurora/shared-client/components/useAuthFlow';
import useMutationWithTracing from '@aurora/shared-client/components/useMutationWithTracing';
import useRegistrationStatus from '@aurora/shared-client/components/users/useRegistrationStatus';
import { hasErrors } from '@aurora/shared-client/helpers/apollo/ApolloHelper';
import useEndUserRoutes from '@aurora/shared-client/routes/useEndUserRoutes';
import { AuthFlow } from '@aurora/shared-client/types/enums';
import type { ContextUserFragment } from '@aurora/shared-generated/types/graphql-types';
import { LocalStorageKeys } from '@aurora/shared-types/community/enums';
import { EndUserComponent } from '@aurora/shared-types/pages/enums';
import { isPendingActionExpired } from '@aurora/shared-utils/helpers/anonymousUserActions/AnonymousUserActionsHelper';
import { getLog } from '@aurora/shared-utils/log';
import type { GraphQLFormattedError } from 'graphql';
import dynamic from 'next/dynamic';
import React, { useContext, useEffect, useState } from 'react';
import { Dropdown, useClassNameMapper } from 'react-bootstrap';
import { useUIDSeed } from 'react-uid';
import { useLocalStorage } from 'react-use';
import type { AnonymousUserAction } from '../../../types';
import { AnonAction } from '../../../types/enums';
import type {
  AnonymousReportAbuseMutation,
  AnonymousReportAbuseMutationVariables,
  ReportAbuseMutation,
  ReportAbuseMutationVariables
} from '../../../types/graphql-types';
import anonymousReportAbuseMutation from '../../messages/MessageActionReportAbuse/ReportAbuseModal/AnonymousReportAbuse.mutation.graphql';
import reportAbuseMutation from '../../messages/MessageActionReportAbuse/ReportAbuseModal/ReportAbuse.mutation.graphql';
import useTranslation from '../../useTranslation';
import type { ReportUserFormData } from '../ReportUserModal/ReportUserModal';

const log = getLog(module);

const ReportUserModal = dynamic(() => import('../ReportUserModal/ReportUserModal'), {
  ssr: false
});

interface Props {
  /**
   * The user whose account is to be reported
   */
  contextUser: ContextUserFragment;
}

/**
 * Renders a modal for reporting User as inappropriate.
 *
 * @constructor
 * @author Janani A
 */
const ReportUserAction: React.FC<React.PropsWithChildren<Props>> = ({ contextUser }) => {
  const cx = useClassNameMapper();
  const { formatMessage } = useTranslation(EndUserComponent.REPORT_USER_ACTION);
  const { addToast } = useContext(ToastContextProvider);
  const { isAnonymous } = useRegistrationStatus();
  const uidSeed = useUIDSeed();
  const { router } = useEndUserRoutes();
  const { triggerAuthFlow } = useAuthFlow();
  const tenant = useContext(TenantContext);
  const {
    publicConfig: { multiAuthEnabled }
  } = tenant;
  const [reportAbuse] = useMutationWithTracing<ReportAbuseMutation, ReportAbuseMutationVariables>(
    module,
    reportAbuseMutation
  );
  const [anonymousReportAbuse] = useMutationWithTracing<
    AnonymousReportAbuseMutation,
    AnonymousReportAbuseMutationVariables
  >(module, anonymousReportAbuseMutation);
  const [frameEnd] = useFrameEnd();
  const [showReportUserModal, setShowReportUserModal] = useState<boolean>(false);

  const [anonymousReportAbuseAction, setAnonymousReportAbuseAction] =
    useLocalStorage<AnonymousUserAction>(LocalStorageKeys.ANONYMOUS_USER_ABUSE_REPORT_KEY, null);

  useEffect(
    () => {
      if (
        anonymousReportAbuseAction &&
        anonymousReportAbuseAction.action === AnonAction.REPORT_ABUSE &&
        anonymousReportAbuseAction.context.userId === contextUser.id
      ) {
        if (!isPendingActionExpired(anonymousReportAbuseAction)) {
          const userAction: AnonymousUserAction = {
            ...anonymousReportAbuseAction,
            timeStamp: Date.now()
          };
          setShowReportUserModal(true);
          setAnonymousReportAbuseAction(userAction);
        } else {
          setShowReportUserModal(false);
          setAnonymousReportAbuseAction(null);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  /**
   * Renders success/failure toast messages
   * @param alertVariant The variant for the alert message
   * @param title Title to be displayed
   * @param body Toast body
   * @param autohide whether the toast should be hidden automatically after certain time interval
   */
  function renderToast(
    alertVariant: ToastAlertVariant,
    title: string,
    body: string,
    autohide = true
  ): void {
    const toastVariant =
      alertVariant === ToastAlertVariant.DANGER ? ToastVariant.BANNER : ToastVariant.FLYOUT;
    const toastProps: ToastProps = {
      id: uidSeed(title),
      toastVariant,
      alertVariant,
      title,
      autohide,
      message: body,
      delay: 4000
    };
    addToast(toastProps);
  }

  async function triggerLoginFlow(formData: ReportUserFormData): Promise<void> {
    await triggerAuthFlow(
      () => {
        setShowReportUserModal(false);
        setAnonymousReportAbuseAction({
          action: AnonAction.REPORT_ABUSE,
          context: {
            userId: contextUser.id,
            reportReason: formData.reportReason,
            reporterComment: formData.reporterComment,
            reportedFor: formData.reportedFor
          },
          timeStamp: Date.now()
        });
      },
      multiAuthEnabled ? AuthFlow.MULTI_AUTH_LOGIN : AuthFlow.LOGIN,
      router.getCurrentRouteAndParams(),
      router.path,
      {
        alertMessage: formatMessage('anonymousReportUser.email.validate.emailAlreadyExists'),
        alertVariant: BannerAlertVariant.WARNING
      }
    );
  }

  async function onReportAbuseCompleted(
    formData: ReportUserFormData,
    mutationErrors: unknown[] | readonly GraphQLFormattedError[]
  ): Promise<void> {
    if (mutationErrors) {
      log.error(
        'Unable to report user abuse: %s. Errors: %d',
        contextUser.id,
        mutationErrors.map(error => error.message)
      );
      if (mutationErrors.some(knownError => knownError.__typename === 'DuplicateEmail')) {
        await triggerLoginFlow(formData);
      } else {
        renderToast(
          ToastAlertVariant.DANGER,
          formatMessage('unexpected.error.title'),
          formatMessage('unexpected.error.message')
        );
      }
    } else {
      setShowReportUserModal(false);
      setAnonymousReportAbuseAction(null);
      renderToast(
        ToastAlertVariant.SUCCESS,
        formatMessage('reportUser.success.title'),
        formatMessage('reportUser.success.message')
      );
    }
  }

  /**
   * Handles form submit. If action is "submit", it makes the mutation call for reporting the User,
   * else it hides the modal.
   * @param form ReportUserFormData form
   * @param action the form action
   */
  async function handleSubmit(form: ReportUserFormData, action: string): Promise<void> {
    if (action === 'submit') {
      const reportAbuseMutationVariables: ReportAbuseMutationVariables = {
        entityId: contextUser.id,
        reporterComment: form.reporterComment,
        reportReason: form.reportReason,
        reportedFor: form.reportedFor
      };

      if (isAnonymous) {
        const { data } = await anonymousReportAbuse({
          variables: {
            ...(reportAbuseMutationVariables as AnonymousReportAbuseMutationVariables),
            anonymousUserInput: {
              firstName: form.firstName,
              lastName: form.lastName,
              email: form.email
            }
          },
          onCompleted: (d): void => {
            if (!hasErrors(d.anonymousReportAbuse)) {
              const parentFrameId = ParentFrameSupplier.getParentFrameId();
              frameEnd({
                context: { parentFrameId }
              });
            }
          }
        });
        onReportAbuseCompleted(
          form,
          (data as AnonymousReportAbuseMutation)?.anonymousReportAbuse.errors
        );
      } else {
        const { errors } = await reportAbuse({
          variables: reportAbuseMutationVariables,
          onCompleted: (d): void => {
            if (!hasErrors(d.reportAbuse)) {
              const parentFrameId = ParentFrameSupplier.getParentFrameId();
              frameEnd({
                context: { parentFrameId }
              });
            }
          }
        });
        onReportAbuseCompleted(form, errors);
      }
    }
    if (action === 'cancel') {
      setShowReportUserModal(false);
      setAnonymousReportAbuseAction(null);
      return;
    }
    setShowReportUserModal(false);
  }

  return (
    <>
      <Dropdown.Item
        key={'reportUser'}
        onClick={() => setShowReportUserModal(true)}
        className={cx('lia-dropdown-item')}
      >
        {formatMessage('reportUser.title')}
      </Dropdown.Item>
      {showReportUserModal && (
        <ReportUserModal
          show={showReportUserModal}
          onHide={() => {
            setShowReportUserModal(false);
            setAnonymousReportAbuseAction(null);
          }}
          onSubmit={handleSubmit}
        />
      )}
    </>
  );
};

export default ReportUserAction;
